/**
 * The items an agent run covers — one copy of a helper that four batch
 * runners (ATC, Twins, Nexus, Value Estimator) used to carry privately.
 *
 * Scope types (src/lib/agentRunService.ts RunScope):
 *   all_empty       every live item, narrowed by the agent's own "nothing yet" filter when it has one
 *   since_last_run  items created after the agent's last completed run (same filter)
 *   date_range      items created inside [from, to]
 *   pool / cycle    the set someone is deciding about, via resolveScopeItemIds;
 *                   an empty membership scopes to nothing, never to everything
 *   track           the track's item pool (item_pools.track_id → priority_ids)
 *   collection      the collection's resolved membership — a smart collection's
 *                   rule is resolved by the collections store, as everywhere else
 *   items           the rows a person chose on the items list; none chosen scopes to nothing
 */

import { getSupabaseClient } from './supabaseClient';
import { getLastCompletedRun, type RunScope } from './agentRunService';
import { resolveScopeItemIds } from './crossPoolSweepService';
import { useCollectionsStore } from '../stores/prioritization/collectionsStore';
import { useItemsStore } from '../stores/prioritization/itemsStore';

export interface ScopeItem {
  id: string;
  title: string;
  description: string;
  category?: string;
}

export interface FetchItemsForScopeOptions {
  /** Whose last completed run bounds `since_last_run`. */
  agentType: string;
  /**
   * PostgREST `.or()` filter for "this agent has nothing on the item yet",
   * applied to `all_empty` and `since_last_run`. Omit to scan every live item.
   */
  emptyFilter?: string;
  /** exit_status values that count as live. Default: active, in_review, blocked. */
  exitStatuses?: string[];
  /** Hard cap on rows returned. */
  limit?: number;
}

/** How many chosen ids one request carries (36-character ids; a request's address has a length limit). */
export const ITEMS_SCOPE_CHUNK = 100;

function toScopeItems(rows: Record<string, unknown>[]): ScopeItem[] {
  return rows.map((row) => ({
    id: row.id as string,
    title: (row.title as string) ?? '',
    description: (row.description as string) ?? '',
    category: (row.item_category as string | null) ?? undefined,
  }));
}

export async function fetchItemsForScope(
  workspaceId: string,
  scope: RunScope,
  options: FetchItemsForScopeOptions,
): Promise<{ items: ScopeItem[]; error: string | null }> {
  try {
    const supabase = getSupabaseClient();
    const live = () =>
      supabase
        .from('items')
        .select('id, title, description, item_category, created_at')
        .eq('workspace_id', workspaceId)
        .in('exit_status', options.exitStatuses ?? ['active', 'in_review', 'blocked'])
        .not('lifecycle_state', 'eq', 'deleted');
    let query = live();

    if (scope.type === 'all_empty') {
      if (options.emptyFilter) query = query.or(options.emptyFilter);
    } else if (scope.type === 'since_last_run') {
      const { data: lastRun } = await getLastCompletedRun(workspaceId, options.agentType);
      if (lastRun?.completedAt) query = query.gte('created_at', lastRun.completedAt);
      if (options.emptyFilter) query = query.or(options.emptyFilter);
    } else if (scope.type === 'date_range') {
      if (scope.from) query = query.gte('created_at', scope.from);
      if (scope.to) query = query.lte('created_at', scope.to);
    } else if (scope.type === 'pool' || scope.type === 'cycle') {
      const ids = await resolveScopeItemIds(workspaceId, scope);
      if (!ids || ids.length === 0) return { items: [], error: null };
      query = query.in('id', ids);
    } else if (scope.type === 'track') {
      if (!scope.trackId) return { items: [], error: 'Pick a track.' };
      const { data: pools, error: poolError } = await supabase
        .from('item_pools')
        .select('priority_ids')
        .eq('workspace_id', workspaceId)
        .eq('track_id', scope.trackId);
      if (poolError) return { items: [], error: poolError.message };
      const ids = [...new Set(((pools ?? []) as Array<{ priority_ids: string[] | null }>).flatMap((p) => p.priority_ids ?? []))];
      if (!ids.length) return { items: [], error: null };
      query = query.in('id', ids);
    } else if (scope.type === 'collection') {
      if (!scope.collectionId) return { items: [], error: 'Pick a collection.' };
      const { itemIds } = useCollectionsStore.getState().getResolvedMembership(scope.collectionId, useItemsStore.getState().items);
      if (!itemIds.length) return { items: [], error: null };
      query = query.in('id', itemIds);
    } else if (scope.type === 'items') {
      const ids = [...new Set(scope.itemIds ?? [])];
      if (!ids.length) return { items: [], error: null };
      // A long id list does not fit in one request's address, so a large
      // selection is read in parts and put back in the order every scope uses.
      const rows: Record<string, unknown>[] = [];
      for (let i = 0; i < ids.length; i += ITEMS_SCOPE_CHUNK) {
        const { data, error } = await live().in('id', ids.slice(i, i + ITEMS_SCOPE_CHUNK));
        if (error) return { items: [], error: error.message };
        rows.push(...((data ?? []) as Record<string, unknown>[]));
      }
      rows.sort((a, b) => String(a.created_at ?? '').localeCompare(String(b.created_at ?? '')));
      return { items: toScopeItems(options.limit ? rows.slice(0, options.limit) : rows), error: null };
    } else {
      // An unknown scope scans nothing, never everything.
      return { items: [], error: `Unknown scope ${String((scope as { type: string }).type)}.` };
    }

    query = query.order('created_at', { ascending: true });
    if (options.limit) query = query.limit(options.limit);

    const { data, error } = await query;
    if (error) return { items: [], error: error.message };
    return { items: toScopeItems((data ?? []) as Record<string, unknown>[]), error: null };
  } catch (err) {
    return { items: [], error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
